import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { getCurrentSessionId, deleteSession, getCurrentUser, getActiveSessionId, clearAllSessionData } from '../../utils/sessionStorage';
import { getSessionFromCloud } from '../../utils/supabaseService';

const Header = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    { label: 'Escanear QR', path: '/qr-code-scanner', icon: 'QrCode' },
    { label: 'Vista General', path: '/bill-overview', icon: 'Receipt' },
    { label: 'Seleccionar Items', path: '/item-selection', icon: 'ShoppingCart' },
    { label: 'Resumen de Pago', path: '/payment-summary', icon: 'CreditCard' },
  ];

  // NOTE: 'Gestión de Sesiones' is intentionally NOT in the header for normal users.
  // The route /session-management remains available if someone (developer) visits it directly.
  // For regular users we show 'Salir de la sesión' which will remove the local session and return to the home screen.
  const secondaryItems = [
    { label: 'Salir de la sesión', path: 'sign-out', icon: 'LogOut' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const toggleMoreMenu = (e) => {
    e?.stopPropagation();
    setIsMoreOpen(prev => !prev);
  };

  // Cerrar menús al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = () => {
      setIsMoreOpen(false);
    };
    if (isMoreOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMoreOpen]);

  const handleNavigation = async (path) => {
    // Special-case sign-out
    if (path === 'sign-out') {
      // Limpiar todos los datos de sesión
      clearAllSessionData();
      navigate('/');
      setIsMobileMenuOpen(false);
      setIsMoreOpen(false);
      return;
    }

    // Para el escáner QR, simplemente navegar (sin borrar sesión actual)
    if (path === '/qr-code-scanner' || path === '/receipt-scanner' || path === '/') {
      navigate(path);
      setIsMobileMenuOpen(false);
      setIsMoreOpen(false);
      return;
    }

    // Para otras páginas, intentar recuperar la sesión actual
    const storedUser = getCurrentUser();
    const sessionId = getActiveSessionId();

    if (sessionId && storedUser) {
      try {
        // Cargar datos frescos de la sesión desde Supabase
        const result = await getSessionFromCloud(sessionId);

        if (result.success && result.data) {
          console.log('📦 Navegando con datos de sesión:', sessionId);
          navigate(path, {
            state: {
              sessionId,
              billData: result.data,
              userName: storedUser.userName,
              userId: storedUser.userId,
              isHost: storedUser.isHost,
              isGuest: !storedUser.isHost
            }
          });
        } else {
          // Si no hay datos en Supabase, navegar sin state
          console.warn('⚠️ No se encontraron datos de sesión en Supabase');
          navigate(path);
        }
      } catch (error) {
        console.error('Error cargando sesión para navegación:', error);
        navigate(path);
      }
    } else {
      // No hay sesión activa, navegar normalmente
      console.log('ℹ️ No hay sesión activa, navegando sin datos');
      navigate(path);
    }

    setIsMobileMenuOpen(false);
    setIsMoreOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <>
      {/* Banner de Donación */}
      <div className="w-full bg-primary/10 border-b border-primary/20 py-2 px-4 overflow-hidden relative group transition-colors hover:bg-primary/15">
        <div className="container mx-auto flex items-center justify-center gap-3 text-center">
          <div className="flex -space-x-1">
            <span role="img" aria-label="coffee" className="text-lg animate-bounce">☕</span>
          </div>
          <p className="text-sm font-medium text-foreground">
            ¿Te gusta <span className="text-primary font-bold">SplitBill</span>? ¡Invítanos a un café para apoyar el proyecto!
          </p>
          <a
            href="https://buymeacoffee.com/logicmindstudio"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary text-white text-xs font-bold rounded-full shadow-sm hover:bg-primary/90 transition-all hover:scale-105 active:scale-95"
          >
            Invitar <Icon name="ExternalLink" size={12} />
          </a>
        </div>
      </div>

      <header className="sticky top-0 z-50 w-full bg-surface border-b border-border shadow-elevation-1">
        <div className="flex h-16 items-center justify-between px-4 lg:px-6">
          {/* Logo */}
          <div className="flex items-center">
            <div
              className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleLogoClick}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <Icon name="Receipt" size={20} color="white" />
              </div>
              <span className="text-xl font-semibold text-foreground font-inter">
                SplitBill
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navigationItems?.map((item) => (
              <Button
                key={item?.path}
                variant="ghost"
                className="text-sm font-medium text-text-secondary hover:text-foreground transition-colors"
                onClick={() => handleNavigation(item?.path)}
                iconName={item?.icon}
                iconPosition="left"
                iconSize={16}
              >
                {item?.label}
              </Button>
            ))}

            {/* More Menu */}
            <div className="relative ml-2">
              <Button
                variant="ghost"
                className="text-sm font-medium text-text-secondary hover:text-foreground"
                iconName="MoreHorizontal"
                iconSize={16}
                onClick={toggleMoreMenu}
              >
                Más
              </Button>

              {isMoreOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-surface border border-border rounded shadow-lg z-50">
                  <div className="py-1">
                    {secondaryItems?.map((item) => (
                      <button
                        key={item?.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-muted hover:text-foreground"
                      >
                        <div className="flex items-center gap-2">
                          <Icon name={item.icon} size={14} />
                          <span>{item.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
              className="h-10 w-10"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
            </Button>
          </div>
        </div>
        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-surface">
            <div className="px-4 py-2 space-y-1">
              {navigationItems?.map((item) => (
                <Button
                  key={item?.path}
                  variant="ghost"
                  fullWidth
                  className="justify-start text-sm font-medium text-text-secondary hover:text-foreground"
                  onClick={() => handleNavigation(item?.path)}
                  iconName={item?.icon}
                  iconPosition="left"
                  iconSize={16}
                >
                  {item?.label}
                </Button>
              ))}

              <div className="pt-2 border-t border-border mt-2">
                {secondaryItems?.map((item) => (
                  <Button
                    key={item?.path}
                    variant="ghost"
                    fullWidth
                    className="justify-start text-sm font-medium text-text-secondary hover:text-foreground"
                    onClick={() => handleNavigation(item?.path)}
                    iconName={item?.icon}
                    iconPosition="left"
                    iconSize={16}
                  >
                    {item?.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;